import React, { useMemo } from 'react'
import styles from './PrivateQuotationCardUnified.module.scss'
import { CalendarDays, MapPin, Cuboid, DollarSign, Building2, User, ExternalLink } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import usePremiumCheck from '../../hooks/usePremiumCheck'

type Jurisdiction = {
  name?: string | null
} | null

type Organism = {
  name?: string | null
} | null

type MaterialCategory = {
  name?: string | null
} | null

type Material = {
  category?: MaterialCategory
} | null

type SubQuotationMaterial = {
  material?: Material
} | null

type SubQuotation = {
  subQuotation_materials?: SubQuotationMaterial[] | null
} | null

type QuotationUser = {
  id?: number | string | null
  type?: string | null
  razon_social?: string | null
  name?: string | null
  last_name?: string | null
} | null

type PrivateQuotation = {
  id: number | string
  name?: string | null
  zone?: string | null
  startDate?: string | null
  endDate?: string | null
  currency?: string | null
  organismo?: Organism
  jurisdiccion?: Jurisdiction
  user?: QuotationUser
  description?: string | null
  subquotation?: SubQuotation[] | null
}

type UserData = {
  id?: number | string | null
} | null

interface PrivateQuotationCardUnifiedProps {
  item: PrivateQuotation
  userData?: UserData
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  const day = date.getUTCDate().toString().padStart(2, '0')
  const month = (date.getUTCMonth() + 1).toString().padStart(2, '0')
  const year = date.getUTCFullYear()
  return `${day}/${month}/${year}`
}

const PrivateQuotationCardUnified: React.FC<PrivateQuotationCardUnifiedProps> = ({ item, userData }) => {
  const navigate = useNavigate()
  const premiumCheck = usePremiumCheck()

  const requesterName = useMemo(() => {
    if (!item?.user) {
      return ''
    }
    if (item.user.type === 'COMPANY') {
      return item.user.razon_social || ''
    }
    const firstName = item.user.name || ''
    const lastName = item.user.last_name || ''
    return `${firstName} ${lastName}`.trim()
  }, [item?.user])

  const categoryNames = useMemo(() => {
    if (!Array.isArray(item?.subquotation)) {
      return [] as string[]
    }
    const names = item.subquotation.flatMap(subquotation => {
      if (!Array.isArray(subquotation?.subQuotation_materials)) {
        return [] as string[]
      }
      return subquotation.subQuotation_materials
        .map(material => material?.material?.category?.name)
        .filter((name): name is string => Boolean(name))
    })
    return Array.from(new Set(names))
  }, [item?.subquotation])

  const isMyQuotation = useMemo(() => {
    if (!item?.user?.id || userData?.id === undefined || userData?.id === null) {
      return false
    }
    return String(item.user.id) === String(userData.id)
  }, [item?.user?.id, userData?.id])

  const startDate = formatDate(item?.startDate)
  const endDate = formatDate(item?.endDate)

  const handleNavigate = () => {
    if (isMyQuotation) {
      navigate(`/cotizaciondemateriales/${item?.id}`)
    } else {
      navigate(`/cotizaciondemateriales/postulante/${item?.id}`)
    }
  }

  return (
    <article className={styles.card}>
      <div className={styles.body}>
        <div className={styles.header}>
          <h3 className={styles.title}>{item?.name}</h3>
          {item?.description && <p className={styles.description}>{item.description}</p>}
          <div className={styles.user}>
            <div className={styles.userIcon}>
              <User size={18} />
            </div>
            <div className={styles.userInfo}>
              <span className={styles.userLabel}>Solicitante</span>
              <span className={styles.userName}>{requesterName || 'Sin datos'}</span>
            </div>
          </div>
        </div>
        <div className={styles.infoGrid}>
          <div className={styles.infoItem}>
            <div className={styles.infoIcon}>
              <MapPin size={16} />
            </div>
            <div className={styles.infoContent}>
              <span className={styles.infoLabel}>Ubicación</span>
              <span className={styles.infoValue}>
                {item?.jurisdiccion?.name ? `${item.jurisdiccion.name}${item?.zone ? ` - ${item.zone}` : ''}` : item?.zone || 'Sin especificar'}
              </span>
            </div>
          </div>
          {item?.organismo?.name && (
            <div className={styles.infoItem}>
              <div className={styles.infoIcon}>
                <Building2 size={16} />
              </div>
              <div className={styles.infoContent}>
                <span className={styles.infoLabel}>Organismo</span>
                <span className={styles.infoValue}>{item.organismo.name}</span>
              </div>
            </div>
          )}
        </div>
        {categoryNames.length > 0 && (
          <div className={styles.categories}>
            {categoryNames.map(category => (
              <span key={category} className={styles.category}>
                {category}
              </span>
            ))}
          </div>
        )}
        <div className={styles.highlight}>
          <div className={styles.highlightRow}>
            <div className={styles.highlightIcon}>
              <CalendarDays size={18} />
            </div>
            <div className={styles.highlightContent}>
              <span className={styles.highlightLabel}>Período de cotización</span>
              <span className={styles.highlightValue}>
                {startDate && endDate ? `${startDate} - ${endDate}` : startDate || endDate || 'Sin fecha definida'}
              </span>
            </div>
          </div>
          <div className={styles.highlightRow}>
            <div className={styles.highlightIcon}>
              <DollarSign size={18} />
            </div>
            <div className={styles.highlightContent}>
              <span className={styles.highlightLabel}>Moneda</span>
              <span className={styles.highlightValue}>{item?.currency || 'Sin especificar'}</span>
            </div>
          </div>
        </div>
        <div className={styles.footer}>
          <button className={styles.cta} onClick={premiumCheck(handleNavigate)} aria-label={`${isMyQuotation ? 'Ver detalles de' : 'Cotizar'} ${item?.name || 'cotización'}`}>
            <span>{isMyQuotation ? 'Ver detalles' : 'Cotizar'}</span>
            <ExternalLink size={16} />
          </button>
        </div>
      </div>
    </article>
  )
}

export default PrivateQuotationCardUnified

